/** The pieces of a composer both pages draw alike: what waits to go, a voice note being recorded, and the video note sheet. */
import { lenLabel } from "../assets/library";
import { api } from "../lib/api";
import type { LiveNoteUpload } from "../lib/liveNoteUpload";
import { RecorderDialog } from "../components/recorder/RecorderDialog";
import { Button, IconButton } from "../components/ui/Button";
import { NOTE_MAX_SEC } from "../lib/noteLimits";
import { AttThumb, NoteBars, NoteCard } from "./attachments";
import type { Pending } from "./useComposer";
import type { useNoteRecorder } from "./useNoteRecorder";

/** The file or the note waiting beside the words, with a way to take it off. */
export function PendingCard({ pending, host, onDrop }: { pending: Pending; host: { name: string; photo?: string | null }; onDrop: () => void }) {
  if (pending.type === "file") {
    return (
      <div className="cattc">
        <span className="as-pf-t">{pending.thumb ? <img src={pending.thumb} alt="" /> : <AttThumb a={pending.att} />}</span>
        <span className="sp">
          <b>{pending.att.name}</b>
          <span>{pending.att.size ?? ""}</span>
        </span>
        <IconButton icon="x" small label="Take it off" onClick={onDrop} />
      </div>
    );
  }
  const status =
    pending.state === "ready"
      ? undefined
      : pending.state === "failed"
        ? "Didn't upload"
        : pending.state === "processing"
          ? "Getting it ready"
          : `Uploading ${Math.round(pending.pct)}%`;
  return (
    <div className="cattn">
      <NoteCard
        a={pending.att ?? { kind: pending.kind, name: pending.kind === "voice" ? "Voice note" : "Video note", duration: lenLabel(pending.seconds) }}
        local={pending.preview}
        host={host}
        className="cnote"
        status={status}
      />
      <IconButton icon="x" small label="Take it off" onClick={onDrop} />
    </div>
  );
}

/** A voice note being recorded, in the composer: the clock, Cancel and Done. */
export function VoiceRec({ seconds, recorder }: { seconds: number; recorder: Pick<ReturnType<typeof useNoteRecorder>, "cancel" | "done"> }) {
  return (
    <div className="crec">
      <span className="crec-dot" />
      <div className="sp">
        <b>Recording a voice note</b>
        <span className="crec-t num">{lenLabel(seconds)}</span>
      </div>
      <span className="crec-w">
        <NoteBars n={26} seed={3} />
      </span>
      <Button size="s" variant="q" onClick={recorder.cancel}>
        Cancel
      </Button>
      <Button size="s" variant="p" icon="check" onClick={recorder.done}>
        Done
      </Button>
    </div>
  );
}

/** The shared video recorder as a sheet: a finished take goes to the composer as a note waiting to send. */
export function VideoNoteDialog({ onClose, onUse, layout }: { onClose: () => void; onUse: (blob: Blob, seconds: number, live: LiveNoteUpload | null) => void; layout?: "phone" | "upright" | "side" }) {
  return <RecorderDialog limitSec={NOTE_MAX_SEC.video} layout={layout} onClose={onClose} upload={api.notes} done={{ label: "Use it", icon: "check", onUse }} />;
}
